import { createFileRoute } from '@tanstack/react-router'
import React, { useState } from 'react'
import { useQuery as useConvexQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import { usePreviewUser } from '@/hooks/usePreviewUser'
import { SignInButton, SignUpButton, useAuth } from '@clerk/clerk-react'
import { SongCard } from '@/components/SongCard'

export const Route = createFileRoute('/preview/instrumental')({
  component: PreviewInstrumentalPage,
})

function PreviewInstrumentalPage() {
  const previewUser = usePreviewUser()

  // Show loading while checking preview user
  if (previewUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  return <PreviewInstrumentalPageContent />
}

function PreviewInstrumentalPageContent() {
  const { isSignedIn } = useAuth()
  const [prompt, setPrompt] = useState('')
  const [model, setModel] = useState('TemPolor i3.5')
  const [showSignInModal, setShowSignInModal] = useState(false)

  // Query preview user's instrumental generations
  const instrumentalGenerations = useConvexQuery(api.preview.getPreviewInstrumentals, {})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // If user is not signed in, show sign-in modal
    if (!isSignedIn) {
      setShowSignInModal(true)
      return
    }

    // If signed in, redirect to app/instrumental (this shouldn't happen in preview mode)
    // But handle it just in case
  }

  return (
    <div className="min-h-screen bg-black text-white py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Preview Banner */}
        <div className="mb-4 bg-terminal-green/20 border-2 border-terminal-green p-3 text-center">
          <p className="text-terminal-green font-mono text-sm font-bold">
            [PREVIEW MODE] • Sign up to create and save your instrumentals
          </p>
        </div>

        {/* Header */}
        <div className="mb-8 terminal-border p-6">
          <h1 className="text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
            [INSTRUMENTAL MUSIC GENERATION]
          </h1>
          <p className="text-trail-tan font-mono mb-2">
            Generate AI-powered instrumental music without vocals using Tempolor
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Generation Form */}
          <div className="lg:col-span-2">
            <div className="terminal-border p-6 bg-black">
              <h2 className="text-2xl font-bold text-terminal-green mb-4 font-mono">
                ═══ GENERATE INSTRUMENTAL ═══
              </h2>

              {/* Sign In Modal */}
              {showSignInModal && (
                <div className="bg-black border-2 border-terminal-green p-4 mb-6">
                  <p className="text-terminal-green font-mono font-bold text-sm mb-4">
                    Sign in to generate instrumental music
                  </p>
                  <div className="flex gap-3">
                    <SignUpButton mode="modal">
                      <button
                        type="button"
                        className="px-6 py-3 bg-terminal-green text-black font-bold font-mono text-sm border-2 border-terminal-green hover:bg-black hover:text-terminal-green transition-all"
                      >
                        [SIGN UP]
                      </button>
                    </SignUpButton>
                    <SignInButton mode="modal">
                      <button
                        type="button"
                        className="px-6 py-3 bg-black text-trail-tan font-bold font-mono text-sm border-2 border-trail-tan hover:bg-trail-tan hover:text-black transition-all"
                      >
                        [SIGN IN]
                      </button>
                    </SignInButton>
                    <button
                      type="button"
                      onClick={() => setShowSignInModal(false)}
                      className="px-6 py-3 bg-black text-gray-400 font-bold font-mono text-sm border-2 border-gray-600 hover:bg-gray-900 transition-all"
                    >
                      [CANCEL]
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <p className="text-trail-tan font-mono text-sm mb-4">
                    Describe the style and mood of instrumental music you want to create. The AI will
                    generate music without any vocals.
                  </p>
                </div>

                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► PROMPT <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g., Upbeat electronic dance music with energetic synths and driving bassline"
                    rows={4}
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono focus:border-terminal-green focus:outline-none resize-none retro-glow transition-all"
                    required
                  />
                  <p className="text-trail-tan text-xs font-mono mt-2">
                    💡 Describe the genre, style, mood, and instruments you want in your instrumental music
                  </p>
                </div>

                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► MODEL
                  </label>
                  <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono focus:border-terminal-green focus:outline-none retro-glow transition-all"
                  >
                    <option value="TemPolor i3.5">TemPolor i3.5 (Latest - Recommended)</option>
                    <option value="TemPolor i3.0">TemPolor i3.0</option>
                    <option value="TemPolor i2.0">TemPolor i2.0</option>
                  </select>
                </div>

                <div className="bg-black border-2 border-trail-tan p-4">
                  <p className="text-trail-tan font-mono text-sm mb-2">
                    <span className="text-sky-blue">►</span> What you&apos;ll get:
                  </p>
                  <ul className="space-y-1 text-trail-tan font-mono text-xs">
                    <li><span className="text-terminal-green">•</span> AI-generated instrumental music</li>
                    <li><span className="text-terminal-green">•</span> No vocals - pure music</li>
                    <li><span className="text-terminal-green">•</span> High-quality MP3 and WAV formats</li>
                    <li><span className="text-terminal-green">•</span> Ready for use in projects</li>
                  </ul>
                </div>

                <button
                  type="submit"
                  disabled={!prompt.trim()}
                  className="w-full px-8 py-4 bg-terminal-green text-black font-bold font-mono text-lg border-4 border-terminal-green hover:bg-black hover:text-terminal-green disabled:bg-gray-700 disabled:border-gray-700 disabled:text-gray-500 transition-all retro-glow tracking-wider"
                >
                  ► GENERATE INSTRUMENTAL ◄
                </button>
              </form>
            </div>
          </div>

          {/* Sidebar - Recent Generations */}
          <div className="lg:col-span-1">
            <div className="terminal-border p-6 bg-black sticky top-8">
              <h2 className="text-xl font-bold text-sky-blue mb-4 font-mono">
                [RECENT INSTRUMENTALS]
              </h2>
              {instrumentalGenerations === undefined ? (
                <p className="text-trail-tan font-mono text-sm">Loading...</p>
              ) : instrumentalGenerations.length === 0 ? (
                <p className="text-trail-tan font-mono text-sm">
                  No instrumentals generated yet. Sign in to create your first one!
                </p>
              ) : (
                <div className="space-y-4">
                  {instrumentalGenerations.map((gen) => (
                    <SongCard
                      key={gen._id}
                      generation={{
                        ...gen,
                        status: gen.status as 'pending' | 'processing' | 'completed' | 'failed',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
